import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout';
import { SelectionCard } from '../../components/SelectionCard';
import { MultiSelectChips } from '../../components/ui/MultiSelectChips';
import { Toggle } from '../../components/ui/Toggle';
import { OrganizerProfile } from '../../types/profile';
const STEPS = [{
  id: 'basics',
  title: 'Event Basics',
  description: 'Key event details'
}, {
  id: 'audience',
  title: 'Audience Profile',
  description: 'Who attends?'
}, {
  id: 'needs',
  title: 'Sponsorship Needs',
  description: 'What do you need?'
}, {
  id: 'media',
  title: 'Media & Exposure',
  description: 'Reach and coverage'
}, {
  id: 'logistics',
  title: 'Logistics',
  description: 'Space and requirements'
}, {
  id: 'summary',
  title: 'Review',
  description: 'Confirm event details'
}];
const ATTENDANCE_RANGES = ['Under 100', '100 - 500', '500 - 1,000', '1,000 - 5,000', '5,000+'];
const AUDIENCE_TYPES = ['Students', 'Professionals', 'Families', 'Tech Enthusiasts', 'Creatives', 'Local Community', 'VIPs'];
const AGE_GROUPS = ['Gen Z (18-24)', 'Millennials (25-40)', 'Gen X (41-56)', 'Boomers (57+)'];
const SPONSOR_CATEGORIES = ['Food & Beverage', 'Tech', 'Fashion', 'Health', 'Financial', 'Any'];
const OFFERINGS = ['Logo Placement', 'Booth Space', 'Speaking Opportunity', 'Social Media Shoutout', 'Product Sampling', 'VIP Access'];
const ACTIVATION_SPACES = ['Small (Table)', 'Medium (10x10)', 'Large (20x20+)', 'Multiple Locations', 'Not Sure'];
export function OrganizerOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<OrganizerProfile>>({
    eventName: '',
    description: '',
    eventDate: '',
    location: '',
    attendeeCount: '',
    audienceTypes: [],
    audienceAgeGroups: [],
    desiredSponsorCategories: [],
    sponsorOfferings: [],
    sponsorshipFeatures: [],
    socialMediaLinks: [],
    digitalReach: '',
    pressCoverage: 'No',
    activationSpace: ''
  });
  const updateData = (key: keyof OrganizerProfile, value: any) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    } else {
      console.log('Submitting Organizer Profile:', formData);
      navigate('/dashboard/organizer');
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        // Basics
        return <div className="space-y-6" data-id="element-2692">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2693">Event Basics</h3>
            <div data-id="element-2694">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2695">
                Event Name
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" value={formData.eventName} onChange={e => updateData('eventName', e.target.value)} data-id="element-2696" />
            </div>
            <div data-id="element-2697">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2698">
                Description
              </label>
              <textarea className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" rows={3} value={formData.description} onChange={e => updateData('description', e.target.value)} data-id="element-2699" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-2700">
              <div data-id="element-2701">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2702">
                  Date
                </label>
                <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg" value={formData.eventDate} onChange={e => updateData('eventDate', e.target.value)} data-id="element-2703" />
              </div>
              <div data-id="element-2704">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2705">
                  Location (City)
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="e.g. New York, NY" value={formData.location} onChange={e => updateData('location', e.target.value)} data-id="element-2706" />
              </div>
            </div>
            <div data-id="element-2707">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2708">
                Expected Attendance
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-id="element-2709">
                {ATTENDANCE_RANGES.map(range => <button key={range} onClick={() => updateData('attendeeCount', range)} className={`px-3 py-2 rounded-lg text-sm border ${formData.attendeeCount === range ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-2710">
                    {range}
                  </button>)}
              </div>
            </div>
          </div>;
      case 2:
        // Audience
        return <div className="space-y-8" data-id="element-2711">
            <div data-id="element-2712">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2713">
                Audience Types
              </h3>
              <MultiSelectChips options={AUDIENCE_TYPES} selected={formData.audienceTypes || []} onChange={selected => updateData('audienceTypes', selected)} data-id="element-2714" />
            </div>
            <div data-id="element-2715">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2716">
                Age Groups
              </h3>
              <MultiSelectChips options={AGE_GROUPS} selected={formData.audienceAgeGroups || []} onChange={selected => updateData('audienceAgeGroups', selected)} data-id="element-2717" />
            </div>
          </div>;
      case 3:
        // Needs
        return <div className="space-y-8" data-id="element-2718">
            <div data-id="element-2719">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2720">
                Sponsor Categories Needed
              </h3>
              <MultiSelectChips options={SPONSOR_CATEGORIES} selected={formData.desiredSponsorCategories || []} onChange={selected => updateData('desiredSponsorCategories', selected)} data-id="element-2721" />
            </div>
            <div data-id="element-2722">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2723">
                What can sponsors get?
              </h3>
              <MultiSelectChips options={OFFERINGS} selected={formData.sponsorOfferings || []} onChange={selected => updateData('sponsorOfferings', selected)} data-id="element-2724" />
            </div>
          </div>;
      case 4:
        // Media
        return <div className="space-y-6" data-id="element-2725">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2726">
              Media & Exposure
            </h3>
            <div data-id="element-2727">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2728">
                Social Media Handles
              </label>
              <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="@instagram, @twitter (comma separated)" value={formData.socialMediaLinks?.join(', ')} onChange={e => updateData('socialMediaLinks', e.target.value.split(',').map(s => s.trim()))} data-id="element-2729" />
            </div>
            <div data-id="element-2730">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2731">
                Digital Reach (Followers/Subscribers)
              </label>
              <select className="w-full px-4 py-2 border border-gray-300 rounded-lg" value={formData.digitalReach} onChange={e => updateData('digitalReach', e.target.value)} data-id="element-2732">
                <option value="" data-id="element-2733">Select range...</option>
                <option value="< 1k" data-id="element-2734">Under 1k</option>
                <option value="1k - 10k" data-id="element-2735">1k - 10k</option>
                <option value="10k - 50k" data-id="element-2736">10k - 50k</option>
                <option value="50k+" data-id="element-2737">50k+</option>
              </select>
            </div>
            <div data-id="element-2738">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2739">
                Press Coverage
              </label>
              <div className="flex gap-4" data-id="element-2740">
                {['Yes', 'No', 'Occasional'].map(opt => <label key={opt} className="flex items-center gap-2" data-id="element-2741">
                    <input type="radio" name="press" checked={formData.pressCoverage === opt} onChange={() => updateData('pressCoverage', opt)} className="text-indigo-600 focus:ring-indigo-500" data-id="element-2742" />
                    <span className="text-gray-700" data-id="element-2743">{opt}</span>
                  </label>)}
              </div>
            </div>
          </div>;
      case 5:
        // Logistics
        return <div className="space-y-6" data-id="element-2744">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2745">Logistics</h3>
            <div data-id="element-2746">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2747">
                Activation Space Available
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-2748">
                {ACTIVATION_SPACES.map(space => <SelectionCard key={space} id={space} label={space} isSelected={formData.activationSpace === space} onClick={() => updateData('activationSpace', space)} className="p-3" data-id="element-2749" />)}
              </div>
            </div>
            <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center" data-id="element-2750">
              <p className="text-sm text-gray-500" data-id="element-2751">
                Floor plan upload (Optional)
              </p>
              <button className="mt-2 text-indigo-600 text-sm font-medium" data-id="element-2752">
                Choose file
              </button>
            </div>
          </div>;
      case 6:
        // Summary
        return <div className="space-y-6" data-id="element-2753">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2754">Review Event</h3>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4" data-id="element-2755">
              <SummarySection title="Event Basics" onEdit={() => setCurrentStep(1)} data-id="element-2756">
                <p className="font-medium" data-id="element-2757">{formData.eventName}</p>
                <p className="text-sm text-gray-500" data-id="element-2758">
                  {formData.eventDate} • {formData.location}
                </p>
              </SummarySection>

              <SummarySection title="Audience" onEdit={() => setCurrentStep(2)} data-id="element-2759">
                <div className="flex flex-wrap gap-1" data-id="element-2760">
                  {formData.audienceTypes?.map(t => <span key={t} className="text-xs bg-white border px-2 py-1 rounded" data-id="element-2761">
                      {t}
                    </span>)}
                </div>
              </SummarySection>

              <SummarySection title="Sponsorship" onEdit={() => setCurrentStep(3)} data-id="element-2762">
                <p className="text-sm text-gray-600" data-id="element-2763">
                  Offering: {formData.sponsorOfferings?.join(', ')}
                </p>
              </SummarySection>

              <SummarySection title="Logistics" onEdit={() => setCurrentStep(5)} data-id="element-2764">
                <p className="text-sm text-gray-600" data-id="element-2765">
                  Space: {formData.activationSpace}
                </p>
              </SummarySection>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <OnboardingLayout currentStep={currentStep} totalSteps={STEPS.length} steps={STEPS} title="Event Setup" description="Create your event profile to find sponsors." onBack={handleBack} onNext={handleNext} nextLabel={currentStep === STEPS.length ? 'Publish Event' : 'Next'} data-id="element-2766">
      {renderStepContent()}
    </OnboardingLayout>;
}
function SummarySection({
  title,
  children,
  onEdit
}: {
  title: string;
  children: React.ReactNode;
  onEdit: () => void;
}) {
  return <div className="border-b border-gray-200 pb-4 last:border-0 last:pb-0" data-id="element-2767">
      <div className="flex justify-between items-center mb-2" data-id="element-2768">
        <h4 className="text-sm font-semibold text-gray-900" data-id="element-2769">{title}</h4>
        <button onClick={onEdit} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-2770">
          Edit
        </button>
      </div>
      {children}
    </div>;
}